import { useEffect, useState } from "react";
import { getToolStats, listTools, seedBuiltinTools } from "../api/ops.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Tool {
  id: string;
  name: string;
  displayName: string;
  category: string;
  enabled: boolean;
  rateLimitPerHour: number | null;
  version: string;
}

interface ToolStat {
  toolName: string;
  total: number;
  success: number;
  avgDurationMs: number | null;
}

export function OpsToolsPage() {
  const { token } = useAuth();
  const [tools, setTools] = useState<Tool[]>([]);
  const [stats, setStats] = useState<ToolStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const reload = () => {
    void Promise.all([listTools(token), getToolStats(token)]).then(([t, s]: unknown[]) => {
      setTools((t as { items: Tool[] }).items ?? []);
      setStats((s as { items: ToolStat[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, []);

  const seed = async () => {
    setSeeding(true);
    await seedBuiltinTools(token);
    setSeeding(false);
    reload();
  };

  const getStats = (name: string) => stats.find((s) => s.toolName === name);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Tool Registry</h1>
          <button
            onClick={() => void seed()}
            disabled={seeding}
            className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {seeding ? "Seeding..." : "Seed Built-in Tools"}
          </button>
        </div>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-2">
            {tools.length === 0 && (
              <p className="text-gray-400">
                No tools registered. Click &quot;Seed Built-in Tools&quot; to get started.
              </p>
            )}
            {tools.map((tool) => {
              const stat = getStats(tool.name);
              return (
                <div
                  key={tool.id}
                  className="flex items-center gap-4 rounded-lg border border-gray-800 bg-gray-900 p-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{tool.displayName}</span>
                      <span className="rounded bg-gray-800 px-1.5 py-0.5 text-xs text-gray-400">
                        {tool.category}
                      </span>
                      {!tool.enabled && (
                        <span className="rounded bg-red-900/30 px-1.5 py-0.5 text-xs text-red-400">
                          disabled
                        </span>
                      )}
                    </div>
                    <span className="font-mono text-xs text-gray-500">{tool.name}</span>
                  </div>
                  {stat && (
                    <div className="text-right text-xs text-gray-500">
                      <p>{stat.total} calls</p>
                      <p className="text-green-400">
                        {stat.total > 0 ? Math.round((stat.success / stat.total) * 100) : 0}%
                        success
                      </p>
                    </div>
                  )}
                  {tool.rateLimitPerHour && (
                    <span className="text-xs text-gray-600">{tool.rateLimitPerHour}/hr</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
